import { useState } from 'react';
import { Icon } from './UI/Icon';
import ClipLoader from 'react-spinners/ClipLoader';
import { useRecoilValue } from 'recoil';
import { disableForEnterpriseAtom } from '../Atom/disableForEnterpriseAtom';
import { useTranslation } from 'react-i18next';

export default function ReportExporter({ onExportPdf }) {

  const disableForEnterprise = useRecoilValue(disableForEnterpriseAtom)
  const { t } = useTranslation();

  const categories = [
    { label: t('reportExporter.categories.compliance'), value: 'compliance_nis2', borderColor: 'border-cyan-400', bgColor: 'bg-cyan-50', accentColor: 'accent-cyan-50', disabled: false },
    { label: t('reportExporter.categories.areas'), value: 'ambiti_nis2', borderColor: 'border-cyan-400', bgColor: 'bg-cyan-50', accentColor: 'accent-cyan-50', disabled: false },
    { label: t('reportExporter.categories.criticality'), value: 'nis2_criticita', borderColor: 'border-cyan-400', bgColor: 'bg-cyan-50', accentColor: 'accent-cyan-50', disabled: false },
    { label: t('reportExporter.categories.fileAngel'), value: 'file_angel', borderColor: 'border-amber-400', bgColor: 'bg-amber-50', accentColor: 'accent-amber-50', disabled: disableForEnterprise ?? false },
    { label: t('reportExporter.categories.fileAngelTime'), value: 'file_angel_time_stats', borderColor: 'border-amber-400', bgColor: 'bg-amber-50', accentColor: 'accent-amber-50', disabled: disableForEnterprise ?? false },
    { label: t('reportExporter.categories.cyberSurveillance'), value: 'cyber_angel', borderColor: 'border-green-400', bgColor: 'bg-green-50', accentColor: 'accent-green-50', disabled: false },
    { label: t('reportExporter.categories.browsingAngel'), value: 'browsing_angel', borderColor: 'border-red-400', bgColor: 'bg-red-50', accentColor: 'accent-red-50', disabled: disableForEnterprise ?? false },
    { label: t('reportExporter.categories.browsingAngelTime'), value: 'browsing_angel_time_stats', borderColor: 'border-red-400', bgColor: 'bg-red-50', accentColor: 'accent-red-50', disabled: disableForEnterprise ?? false },
    { label: t('reportExporter.categories.vulnerabilities'), value: 'cyber_vulnerabilita', borderColor: 'border-green-400', bgColor: 'bg-green-50', accentColor: 'accent-green-50', disabled: false },
    { label: t('reportExporter.categories.awarenessTraining'), value: 'awareness_training', borderColor: 'border-purple-400', bgColor: 'bg-purple-50', accentColor: 'accent-purple-50', disabled: false },
    { label: t('reportExporter.categories.networkingSurveillance'), value: 'networking_surveillance', borderColor: 'border-indigo-400', bgColor: 'bg-indigo-50', accentColor: 'accent-indigo-50', disabled: false },
  ];

  const [loading, setLoading] = useState(false);


  const [state, setState] = useState(() =>
    categories.reduce((acc, cat) => {
      acc[cat.value] = !cat.disabled;
      return acc;
    }, {})
  );

  const toggle = (key) => {
    setState((prev) => {
      const newState = { ...prev };
      const currentValue = prev[key];
      
      // Attiva/disattiva il flag corrente
      newState[key] = !currentValue;
      
      // Logica di dipendenza: se attivi una statistica temporale, attiva anche il parent
      if (key === 'file_angel_time_stats' && newState[key] === true) {
        // Se abiliti statistiche temporali File Angel, abilita anche File Angel
        newState['file_angel'] = true;
      }
      if (key === 'browsing_angel_time_stats' && newState[key] === true) {
        // Se abiliti statistiche temporali Browsing Angel, abilita anche Browsing Angel
        newState['browsing_angel'] = true;
      }
      
      // Logica di dipendenza: se disattivi il parent, disattiva anche la statistica temporale
      if (key === 'file_angel' && newState[key] === false) {
        // Se disabiliti File Angel, disabilita anche le statistiche temporali
        newState['file_angel_time_stats'] = false;
      }
      if (key === 'browsing_angel' && newState[key] === false) {
        // Se disabiliti Browsing Angel, disabilita anche le statistiche temporali
        newState['browsing_angel_time_stats'] = false;
      }
      
      return newState;
    });
  };

  const handleExport = async () => {
    setLoading(true);
    const payload = {
      dati_azienda_asset: true,
      ...state,
    };
    try {
      await onExportPdf(payload);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white p-6 rounded-lg shadow border">
      {/* Titolo */}
  <h2 className="text-xl font-semibold text-gray-700 mb-4">{t('reportExporter.selectFields')}</h2>

      <div className="flex-1 overflow-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
        {categories.map((cat) => {
          const selected = state[cat.value];
            
          return (
            <div
              key={cat.value}
              className={`flex items-center gap-3 px-4 py-3 rounded-md border-2 
             ${cat.borderColor} 
             ${selected ? cat.bgColor : 'bg-white'} 
    ${cat.disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer hover:shadow-lg'} 
    shadow-md transition-all duration-200`}
              onClick={() => !cat.disabled && toggle(cat.value)}
            >
              <input
                type="checkbox"
                disabled={cat.disabled}
                checked={selected}
                onChange={() => toggle(cat.value)}
                className={`form-checkbox w-5 h-5 shrink-0 ${cat.accentColor} ${cat.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={(e) => e.stopPropagation()}
              />
              <span
                className={`text-gray-800 font-medium whitespace-pre-line ${cat.disabled ? 'text-gray-400' : ''
                  }`}
              >
                {cat.label}
              </span>
            </div>

          );
        })}
      </div>

      <div className="mt-6 flex justify-end space-x-4">
        {/* <button
          onClick={handleExport}
          className="px-4 py-2 bg-transparent text-black border border-black rounded-md hover:bg-amber-200 hover:text-amber-800 hover:border-amber-800 focus:outline-none focus:ring-2 focus:ring-black transition-all flex items-center gap-2 duration-300"
        >
          <Icon name={'pdf'} />
          Esporta PDF
        </button> */}
        <button
          className="flex w-40 gap-2 justify-center items-center bg-yellow-400 px-5 py-2 text-black font-semibold rounded duration-300 hover:bg-yellow-500 transition-colors active:scale-95 disabled:opacity-50"
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? (
            <div className="flex justify-center w-full">
              <ClipLoader size={24} />
            </div>
          ) : (
            <>
              <Icon name="pdf" /> {t('reportExporter.exportPdf')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
